/**
 * HelpSettings.tsx
 *
 * Standard React help settings page using HTML/CSS
 * Displays help categories and basic documentation
 */
import React, { useState } from 'react';
import { useTheme } from '../../services/ThemeContext';
import { ScrollableModal } from './Modal';

interface HelpSettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

interface HelpTopic {
  question: string;
  answer: string;
}

interface HelpCategory {
  title: string;
  topics: HelpTopic[];
}

const helpCategories: Record<string, HelpCategory> = {
  gettingStarted: {
    title: 'Getting Started',
    topics: [
      {
        question: 'How do I begin a walkthrough?',
        answer: 'Enter a Bible verse reference or topic in the input field on the intro screen. You can optionally set a custom story name and choose between Immersive or Examine display mode, then click "Begin Walkthrough".',
      },
      {
        question: 'What are the display modes?',
        answer: 'Immersive mode focuses on story flow with minimal UI, while Examine mode removes the story theme and allows you to walk through and examine things more carefully.',
      },
    ],
  },
  gameplay: {
    title: 'Gameplay',
    topics: [
      {
        question: 'How do I interact with the story?',
        answer: 'Type your actions or responses in the text input at the bottom. You can also use suggested choices and explore related passages. Leave the input blank and press Continue to let the AI advance the story.',
      },
      {
        question: 'What are directives?',
        answer: 'Directives are instructions you give the AI to shape the story\'s style. You can set default directives in settings or add custom ones for each story.',
      },
      {
        question: 'Can I go back and change my choices?',
        answer: 'Use the Redo button to retry the last AI response with a different outcome.',
      },
    ],
  },
  features: {
    title: 'Features',
    topics: [
      {
        question: 'What are achievements?',
        answer: 'Achievements track your progress through Bible verse exploration. Unlock them by completing turns, spending time in the app, and experiencing different verses.',
      },
      {
        question: 'How do themes work?',
        answer: 'You can choose from several color themes in the settings, including Light Conservative, Dark Conservative, High Contrast (light and dark), and Vivid Dark options.',
      },
      {
        question: 'Can I customize the appearance?',
        answer: 'Yes! Use Font Settings to change text size and font family, and Transparency Settings to adjust text area opacity.',
      },
    ],
  },
};

const HelpSettings: React.FC<HelpSettingsProps> = ({ isOpen, onClose }) => {
  const { currentTheme: colors } = useTheme();
  const [selectedCategory, setSelectedCategory] = useState<keyof typeof helpCategories | null>(null);
  const [selectedTopic, setSelectedTopic] = useState<number | null>(null);

  if (!isOpen) return null;

  // Show topic detail modal
  if (selectedCategory !== null && selectedTopic !== null) {
    const category = helpCategories[selectedCategory];
    const topic = category.topics[selectedTopic];

    return (
      <ScrollableModal
        isOpen={true}
        onClose={() => {
          setSelectedTopic(null);
          setSelectedCategory(null);
        }}
        title={category.title}
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 'bold', marginBottom: 8, color: colors.primary }}>
            {topic.question}
          </div>
          <div style={{ fontSize: 14, lineHeight: '20px', color: colors.textMain }}>
            {topic.answer}
          </div>
        </div>
        <button
          onClick={() => {
            setSelectedTopic(null);
            setSelectedCategory(null);
          }}
          style={{
            width: '100%',
            padding: '12px 16px',
            borderRadius: 10,
            backgroundColor: colors.primary,
            border: 'none',
            color: '#ffffff',
            fontSize: 14,
            fontWeight: '600',
            cursor: 'pointer',
          }}
        >
          Got it
        </button>
      </ScrollableModal>
    );
  }

  // Show category detail
  if (selectedCategory !== null) {
    const category = helpCategories[selectedCategory];

    return (
      <ScrollableModal
        isOpen={true}
        onClose={() => setSelectedCategory(null)}
        title={category.title}
      >
        <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 16 }}>
          {category.topics.length} {category.topics.length === 1 ? 'topic' : 'topics'}
        </div>
        {category.topics.map((topic, index) => (
          <button
            key={index}
            onClick={() => setSelectedTopic(index)}
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 12px',
              backgroundColor: 'transparent',
              border: 'none',
              borderBottomWidth: index < category.topics.length - 1 ? 1 : 0,
              borderBottomColor: colors.border,
              borderBottomStyle: 'solid',
              cursor: 'pointer',
            }}
          >
            <span style={{ color: colors.textMain, fontSize: 14, textAlign: 'left', flex: 1 }}>
              {topic.question}
            </span>
            <span style={{ color: colors.primary }}>→</span>
          </button>
        ))}
      </ScrollableModal>
    );
  }

  // Main help categories
  return (
    <ScrollableModal isOpen={isOpen} onClose={onClose} title="Help">
      {Object.entries(helpCategories).map(([key, category]) => (
        <button
          key={key}
          onClick={() => setSelectedCategory(key as keyof typeof helpCategories)}
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px',
            marginBottom: 12,
            backgroundColor: colors.background,
            borderWidth: 1,
            borderColor: colors.border,
            borderStyle: 'solid',
            borderRadius: 12,
            cursor: 'pointer',
          }}
        >
          <div style={{ flex: 1, textAlign: 'left' }}>
            <div style={{ fontSize: 16, fontWeight: '600', color: colors.primary, marginBottom: 4 }}>
              {category.title}
            </div>
            <div style={{ fontSize: 13, color: colors.textMuted }}>
              {category.topics.length} {category.topics.length === 1 ? 'topic' : 'topics'}
            </div>
          </div>
          <span style={{ fontSize: 18, color: colors.textMuted }}>→</span>
        </button>
      ))}
    </ScrollableModal>
  );
};

export { HelpSettings };
